import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import SignatureCanvas from 'react-signature-canvas';
import { apiService } from '../services/api';
import QRCode from 'qrcode';
import { 
  ArrowLeft, 
  RotateCcw, 
  Check, 
  User,
  Gift,
  Search,
  Shield,
  Sparkles,
  Plus,
  Minus,
  X,
  ChevronUp,
  Edit3,
  Calendar,
  Users,
  Mail
} from 'lucide-react';

// Add CSS animations
const styles = `
  @keyframes shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
  .shimmer {
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent);
    background-size: 200% 100%;
    animation: shimmer 1.5s infinite;
  }
  @keyframes fadeIn {
    0% { opacity: 0; transform: scale(0.8); }
    100% { opacity: 1; transform: scale(1); }
  }
  .fade-in {
    animation: fadeIn 0.3s ease-out forwards;
  }
  .animate-fadeIn {
    animation: fadeIn 0.3s ease-out forwards;
  }
`;

const StreamlinedWaiverForm: React.FC = () => {
  const navigate = useNavigate();
  const signaturePadRef = useRef<SignatureCanvas>(null);

  // Session management utilities
  const generateSessionId = () => {
    return 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  };

  // Environment-aware URL utility
  const getBaseUrl = () => {
    // In development, use localhost for testing
    // In production or when deployed, use the production URL
    if (process.env.NODE_ENV === 'development' && window.location.hostname === 'localhost') {
      return 'http://localhost:3000';
    }
    // Production URL for QR codes that need to work from anywhere
    return 'https://immersa.fun';
  };

  const saveToLocalStorage = (waiverData: any) => {
    const sessionData = {
      sessionId: generateSessionId(),
      waiverData: waiverData,
      timestamp: Date.now(),
      expiresAt: Date.now() + (365 * 24 * 60 * 60 * 1000), // 1 year from now
    };
    localStorage.setItem('immersa_waiver_session', JSON.stringify(sessionData));
    return sessionData.sessionId;
  };

  // Gender detection utility
  const detectGender = (name: string): 'boy' | 'girl' | 'neutral' => {
    const boyNames = ['alex', 'andrew', 'anthony', 'austin', 'benjamin', 'brandon', 'brian', 'caleb', 'cameron', 'carlos', 'christopher', 'connor', 'daniel', 'david', 'diego', 'ethan', 'gabriel', 'ian', 'jackson', 'jacob', 'james', 'jason', 'john', 'jordan', 'jose', 'joshua', 'justin', 'kevin', 'kyle', 'logan', 'lucas', 'luis', 'mason', 'matthew', 'michael', 'nathan', 'nicholas', 'noah', 'owen', 'parker', 'ryan', 'samuel', 'sean', 'sebastian', 'tyler', 'william', 'zachary'];
    const girlNames = ['abigail', 'addison', 'alexandra', 'alexis', 'amanda', 'amber', 'amy', 'andrea', 'angela', 'anna', 'ashley', 'brianna', 'brittany', 'brooke', 'chloe', 'christina', 'destiny', 'elizabeth', 'emily', 'emma', 'gabrielle', 'grace', 'hailey', 'hannah', 'isabella', 'jasmine', 'jennifer', 'jessica', 'jordan', 'julia', 'kaitlyn', 'kayla', 'lauren', 'madison', 'maria', 'mariah', 'mary', 'megan', 'melissa', 'michelle', 'morgan', 'natalie', 'nicole', 'olivia', 'paige', 'rachel', 'rebecca', 'samantha', 'sarah', 'sophia', 'stephanie', 'sydney', 'taylor', 'vanessa', 'victoria'];
    
    const lowerName = name.toLowerCase().trim();
    if (boyNames.includes(lowerName)) return 'boy';
    if (girlNames.includes(lowerName)) return 'girl';
    return 'neutral';
  };

  const getGenderIcon = (gender: 'boy' | 'girl' | 'neutral') => {
    switch (gender) {
      case 'boy': return '👦';
      case 'girl': return '👧';
      case 'neutral': return '🧒';
    }
  };
  
  const [currentStep, setCurrentStep] = useState('lookup'); // lookup, waiver, form, signature, complete
  const [formData, setFormData] = useState({
    lookupPhone: '',
    name: '',
    phone: '',
    email: '',
    wantUpdates: false,
    waiverAccepted: false,
    signedFor: 'myself',
    numKids: 0,
    kids: [] as Array<{name: string, age: number, gender: 'boy' | 'girl' | 'neutral'}>,
    participants: [] as string[],
    birthday: '', // For birthday special offers
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [existingWaiver, setExistingWaiver] = useState<any>(null);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [completedWaiverData, setCompletedWaiverData] = useState<any>(null);
  const [isEditingInfo, setIsEditingInfo] = useState(false);
  const [emailSignupSuccess, setEmailSignupSuccess] = useState(false);
  const [validation, setValidation] = useState({
    phone: true,
    email: true,
    name: true,
  });

  // Phone number formatting
  const formatPhoneNumber = (value: string) => {
    const phoneNumber = value.replace(/\D/g, '');
    if (phoneNumber.length < 4) return phoneNumber;
    if (phoneNumber.length < 7) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    }
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
  };

  // Email validation
  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const commonDomains = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'icloud.com'];
    const domain = email.split('@')[1]?.toLowerCase();
    return emailRegex.test(email) && (commonDomains.includes(domain) || domain?.includes('.'));
  };

  // Phone validation
  const isValidPhone = (phone: string) => {
    const phoneDigits = phone.replace(/\D/g, '');
    return phoneDigits.length === 10;
  };

  // Name validation
  const isValidName = (name: string) => {
    return name.trim().length >= 2 && /^[a-zA-Z\s'-]+$/.test(name.trim());
  };

  // Check existing waiver
  const handleLookupWaiver = async () => {
    if (!isValidPhone(formData.lookupPhone)) {
      setError('Please enter a valid phone number');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Check existing waiver by phone
      console.log('🔍 Looking up waiver for phone:', formData.lookupPhone);
      const response = await apiService.checkWaiverByPhone(formData.lookupPhone);
      console.log('📋 API Response:', response.data);
      
      if (response.data.exists) {
        setExistingWaiver(response.data.waiver);
        console.log('✅ Found existing waiver:', response.data.waiver);
        
        // If waiver is valid, show confirmation page directly
        if (response.data.waiver.isValid) {
          console.log('🎉 Waiver is valid - redirecting to confirmation');
          // Generate QR code for public confirmation URL
          const qrData = response.data.waiver.qrCode || `WAIVER-${response.data.waiver.id}`;
          const baseUrl = getBaseUrl();
          const publicConfirmationUrl = `${baseUrl}/waiver-confirmation/${qrData}`;
          const qrCodeDataUrl = await QRCode.toDataURL(publicConfirmationUrl);
          setQrCodeUrl(qrCodeDataUrl);
          
          // Set completed waiver data for confirmation display
          setCompletedWaiverData({
            ...response.data.waiver,
            isReturningCustomer: true,
            publicConfirmationUrl
          });
          
          // Record the waiver access in backend
          try {
            await apiService.recordWaiverAccess(response.data.waiver.id);
          } catch (accessError) {
            console.error('Failed to record waiver access:', accessError);
          }
          
          setCurrentStep('complete');
        } else {
          console.log('⚠️  Waiver is expired - showing existing page');
          // Invalid/expired waiver, show existing page
          setCurrentStep('existing');
        }
      } else {
        console.log('❌ No existing waiver found - proceeding to new waiver form');
        setCurrentStep('waiver');
      }
    } catch (error: any) {
      console.error('🚨 Error during waiver lookup:', error);
      
      // Check if it's an HTTP error response with waiver data
      if (error.response?.status === 409 && error.response?.data?.waiver) {
        // This is a "valid waiver exists" error - treat it as success
        console.log('📋 Found existing waiver via error response:', error.response.data.waiver);
        setExistingWaiver(error.response.data.waiver);
        setCurrentStep('existing');
      } else if (error.response?.data?.message) {
        // Other API errors
        setError(error.response.data.message);
      } else {
        // No existing waiver, proceed to new waiver
        setCurrentStep('waiver');
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle input changes with validation
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    
    let newValue = value;
    if (name === 'phone' || name === 'lookupPhone') {
      newValue = formatPhoneNumber(value);
    }

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : newValue,
    }));

    // Real-time validation
    if (name === 'phone' || name === 'lookupPhone') {
      setValidation(prev => ({ ...prev, phone: isValidPhone(newValue) }));
    } else if (name === 'email') {
      setValidation(prev => ({ ...prev, email: isValidEmail(value) }));
    } else if (name === 'name') {
      setValidation(prev => ({ ...prev, name: isValidName(value) }));
    }
  };

  // Check if form is complete enough to proceed
  const canCompleteWaiver = () => {
    const hasBasicInfo = formData.waiverAccepted && 
                        formData.name && 
                        validation.name && 
                        formData.phone && 
                        validation.phone &&
                        formData.signedFor && 
                        formData.signedFor !== '';

    console.log('Validation check:', {
      hasBasicInfo,
      signedFor: formData.signedFor,
      kidsCount: formData.kids.length,
      kidsWithNames: formData.kids.filter(kid => kid.name.trim() !== '').length,
      participantsCount: formData.participants.length,
      participantsWithNames: formData.participants.filter(p => p.trim() !== '').length
    });

    // If signing for myself, basic info is enough
    if (formData.signedFor === 'myself') {
      return hasBasicInfo;
    }

    // If signing for family, need at least one kid with a name
    if (formData.signedFor === 'family') {
      const hasValidKids = formData.kids.length > 0 && 
                          formData.kids.some(kid => kid.name.trim() !== '');
      console.log('Family validation:', { hasValidKids, kidsData: formData.kids });
      return hasBasicInfo && hasValidKids;
    }

    // If signing for group, need at least one participant
    if (formData.signedFor === 'group') {
      const hasValidParticipants = formData.participants.length > 0 && 
                                  formData.participants.some(participant => participant.trim() !== '');
      return hasBasicInfo && hasValidParticipants;
    }

    return hasBasicInfo;
  };

  // Check if signature is ready
  const canSubmit = () => {
    const isEmpty = signaturePadRef.current?.isEmpty();
    console.log('Signature canvas isEmpty:', isEmpty);
    return !isEmpty;
  };

  // Handle waiver acceptance
  const handleWaiverAccept = () => {
    setFormData(prev => ({ ...prev, waiverAccepted: true }));
    setCurrentStep('form');
  };

  // Handle complete waiver (go to signature)
  const handleCompleteWaiver = () => {
    if (canCompleteWaiver()) {
      setCurrentStep('signature');
    }
  };

  // Final submission
  const handleSubmit = async () => {
    if (!canSubmit()) {
      setError('Please provide your signature');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const signatureData = signaturePadRef.current?.toDataURL();
      
      if (!signatureData) {
        throw new Error('Failed to capture signature');
      }
      
      const waiverData = {
        name: formData.name,
        phone: formData.phone,
        email: formData.email || '',
        signature: signatureData,
        signedFor: (formData.signedFor === 'myself' ? 'self' : formData.signedFor) as 'self' | 'family' | 'group',
        numKids: formData.numKids,
        kidsAges: formData.kids.map(kid => kid.age),
        kidsDetails: formData.kids.map(kid => ({
          name: kid.name,
          age: kid.age,
          gender: kid.gender
        })),
        participants: formData.participants || [],
        marketingConsent: {
          vrPromotions: formData.wantUpdates,
          stemWorkshops: formData.wantUpdates,
          specialEvents: formData.wantUpdates,
          frequency: 'monthly'
        },
        interests: []
      };

      // Debug: Log what we're sending
      console.log('Sending waiver data:', {
        ...waiverData,
        signature: 'BASE64_DATA_PROVIDED'
      });

      const response = await apiService.createWaiver(waiverData);

      // Generate QR code for public confirmation URL
      const qrCode = response.data?.waiver?.qrCode || `WAIVER-${response.data?.waiver?.id || Date.now()}`;
      const baseUrl = getBaseUrl();
      const publicConfirmationUrl = `${baseUrl}/waiver-confirmation/${qrCode}`;
      
      // Create display data with public URL
      const completedData = {
        waiverDbId: response.data?.waiver?.id || Date.now(),
        qrCode: qrCode,
        publicConfirmationUrl: publicConfirmationUrl,
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        signedFor: formData.signedFor,
        numKids: formData.kids.length,
        numParticipants: formData.participants.length,
        completedAt: new Date().toISOString(),
        signedAt: response.data?.waiver?.signedAt || new Date().toISOString(),
        expiresAt: response.data?.waiver?.expiresAt,
        verificationHash: btoa(`${formData.name}_${formData.phone}_${Date.now()}`).substring(0, 12)
      };

      // Save session to localStorage
      saveToLocalStorage({
        ...waiverData,
        ...completedData
      });

      // Generate QR code that points to public confirmation URL
      const qrCodeDataUrl = await QRCode.toDataURL(publicConfirmationUrl, {
        width: 200,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });

      setQrCodeUrl(qrCodeDataUrl);
      setCompletedWaiverData(completedData);
      setCurrentStep('complete');
      
    } catch (err: any) {
      console.error('🚨 Waiver submission error:', err);
      
      // Check if this is a "valid waiver already exists" error
      if (err.response?.status === 409 && err.response?.data?.waiver) {
        console.log('📋 Valid waiver already exists - showing welcome back flow');
        const existingWaiver = err.response.data.waiver;
        
        // Generate QR code for public confirmation URL
        const qrData = existingWaiver.qrCode || `WAIVER-${existingWaiver.id}`;
        const baseUrl = getBaseUrl();
        const publicConfirmationUrl = `${baseUrl}/waiver-confirmation/${qrData}`;
        const qrCodeDataUrl = await QRCode.toDataURL(publicConfirmationUrl);
        setQrCodeUrl(qrCodeDataUrl);
        
        // Set completed waiver data for confirmation display
        setCompletedWaiverData({
          ...existingWaiver,
          isReturningCustomer: true,
          publicConfirmationUrl
        });
        
        // Record the waiver access in backend
        try {
          await apiService.recordWaiverAccess(existingWaiver.id);
        } catch (accessError) {
          console.error('Failed to record waiver access:', accessError);
        }
        
        setCurrentStep('complete');
      } else {
        // Other errors - show error message
        setError(err.response?.data?.message || err.message || 'Failed to submit waiver');
      }
    } finally {
      setLoading(false);
    }
  };

  const renderContent = () => {
    switch (currentStep) {
      case 'lookup':
        return (
          <div className="space-y-4 sm:space-y-6 lg:space-y-8">
            <div className="text-center">
              <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center mx-auto mb-6 sm:mb-8">
                <Search className="w-10 h-10 sm:w-12 sm:h-12 text-white" />
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3 sm:mb-4">Welcome to Immersa VR!</h2>
              <p className="text-slate-300 text-lg sm:text-xl mb-8 sm:mb-10 px-2">Ready for an amazing VR experience? Let's get started!</p>
            </div>

            <div className="bg-gradient-to-br from-emerald-500/10 via-blue-500/10 to-purple-500/10 rounded-2xl sm:rounded-3xl p-6 sm:p-8 border border-emerald-500/20 mb-6 sm:mb-8 shadow-2xl">
              <div className="flex items-center justify-center mb-4">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r from-emerald-400 to-blue-400 rounded-full flex items-center justify-center mr-3">
                  <Check className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                </div>
                <h3 className="text-xl sm:text-2xl font-semibold text-white">Already signed a waiver?</h3>
              </div>
              <p className="text-emerald-200 text-center mb-2 sm:mb-3 text-base sm:text-lg">Skip the paperwork & redeem your special discount!</p>
              <p className="text-slate-300 text-center text-sm sm:text-base mb-6 sm:mb-8 px-2">Your effort is rewarded - just enter your phone number</p>
              
              <div className="max-w-sm mx-auto">
                <input
                  type="tel"
                  name="lookupPhone"
                  value={formData.lookupPhone}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 sm:px-6 sm:py-4 border border-emerald-400/30 rounded-2xl text-white placeholder-slate-400 focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all backdrop-blur-sm text-center text-lg sm:text-xl font-medium"
                  placeholder="(555) 123-4567"
                />
                <button
                  onClick={handleLookupWaiver}
                  disabled={!isValidPhone(formData.lookupPhone) || loading}
                  className={`w-full mt-4 sm:mt-6 py-3 sm:py-4 rounded-2xl font-semibold transition-all text-base sm:text-lg flex items-center justify-center space-x-2 ${
                    isValidPhone(formData.lookupPhone) && !loading
                      ? 'bg-gradient-to-r from-emerald-600 to-blue-600 text-white hover:from-emerald-700 hover:to-blue-700 shadow-lg hover:shadow-xl transform hover:-translate-y-1'
                      : 'bg-slate-600 text-slate-400 cursor-not-allowed'
                  }`}
                >
                  <Gift className="w-5 h-5" />
                  <span>{loading ? 'Checking...' : 'Check & Get My Discount'}</span>
                </button>
              </div>
            </div>

            <div className="text-center">
              <div className="w-full h-px bg-gradient-to-r from-transparent via-white/20 to-transparent mb-6"></div>
              <p className="text-slate-400 mb-4 sm:mb-6 text-base sm:text-lg">First time visitor?</p>
              <button
                onClick={() => setCurrentStep('waiver')}
                className="group relative text-white bg-gradient-to-r from-purple-600 to-pink-600 px-8 py-4 sm:px-10 sm:py-5 rounded-2xl hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-1 text-lg sm:text-xl"
              >
                <div className="flex items-center justify-center space-x-3">
                  <Sparkles className="w-6 h-6 group-hover:rotate-12 transition-transform" />
                  <span className="font-semibold">Start My VR Adventure</span>
                  <Sparkles className="w-6 h-6 group-hover:-rotate-12 transition-transform" />
                </div>
                <div className="text-purple-200 text-sm sm:text-base mt-2">Quick 2-minute setup</div>
              </button>
            </div>
          </div>
        );

      case 'existing':
        return (
          <div className="text-center space-y-6">
            <div className="w-20 h-20 bg-gradient-to-r from-green-500 to-blue-500 rounded-full flex items-center justify-center mx-auto">
              <Check className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-white">
              {existingWaiver?.isValid ? 'Welcome Back!' : 'Waiver Found!'}
            </h2>
            <div className={`${existingWaiver?.isValid ? 'bg-green-500/10 border-green-500/20' : 'bg-orange-500/10 border-orange-500/20'} border rounded-2xl p-6`}>
              <p className={`${existingWaiver?.isValid ? 'text-green-300' : 'text-orange-300'} text-lg`}>
                {existingWaiver?.isValid 
                  ? `Great to see you again! Your waiver is valid until ${new Date(existingWaiver.expiresAt).toLocaleDateString()}`
                  : 'Your waiver has expired. Please sign a new one.'
                }
              </p>
            </div>
            {existingWaiver?.isValid ? (
              <button
                onClick={() => navigate('/')}
                className="bg-gradient-to-r from-green-600 to-blue-600 text-white px-8 py-3 rounded-xl hover:from-green-700 hover:to-blue-700 transition-all"
              >
                Continue to VR Experience
              </button>
            ) : (
              <button
                onClick={() => setCurrentStep('waiver')}
                className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8 py-3 rounded-xl hover:from-purple-700 hover:to-pink-700 transition-all"
              >
                Sign New Waiver
              </button>
            )}
          </div>
        );

      case 'waiver':
        return (
          <div className="space-y-6">
            {/* Back Button */}
            <div className="flex justify-start">
              <button
                onClick={() => setCurrentStep('lookup')}
                className="flex items-center text-slate-300 hover:text-white transition-colors group"
              >
                <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" />
                <span>Back to Lookup</span>
              </button>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <Shield className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-white mb-4">Safety First!</h2>
              <p className="text-slate-300 text-lg">Please read and accept our waiver</p>
            </div>

            <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
              <h3 className="text-xl font-semibold text-white mb-4">Liability Waiver Agreement</h3>
              <div className="text-sm text-slate-300 max-h-40 overflow-y-auto space-y-3 bg-white/5 p-4 rounded-xl">
                <p>
                  I acknowledge that participation in virtual reality activities involves inherent risks, including but not limited to physical injury, motion sickness, and disorientation.
                </p>
                <p>
                  I voluntarily assume all risks and hazards incidental to such participation and hereby waive, release, and discharge Immersa VR Center from any and all liability, claims, demands, actions, or causes of action.
                </p>
                <p>
                  This waiver is valid for one year from the date of signing and covers all visits during this period.
                </p>
                <p>
                  By signing below, I confirm that I am at least 18 years old or have parental/guardian consent, and I understand and agree to these terms.
                </p>
              </div>

              <div className="mt-6 text-center">
                <button
                  onClick={handleWaiverAccept}
                  className="bg-gradient-to-r from-green-600 to-blue-600 text-white px-8 py-3 rounded-xl font-semibold hover:from-green-700 hover:to-blue-700 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                >
                  I Accept the Terms
                </button>
              </div>
            </div>
          </div>
        );

      case 'form':
        return (
          <div className="space-y-4 sm:space-y-6">
            {/* Back Button */}
            <div className="flex justify-start">
              <button
                onClick={() => setCurrentStep('waiver')}
                className="flex items-center text-slate-300 hover:text-white transition-colors group"
              >
                <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" />
                <span>Back to Safety First</span>
              </button>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                <User className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2 sm:mb-4">Almost Ready!</h2>
              <p className="text-slate-300 text-base sm:text-lg px-2">Just need a few quick details</p>
            </div>

            <div className="space-y-4 sm:space-y-6">
              {/* Required Fields - Show expanded if not filled, collapsed if filled, or if user is editing */}
              {(!formData.name || !formData.phone || !validation.name || !validation.phone || isEditingInfo) ? (
                <div className="bg-white/5 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-white/10">
                  <h3 className="text-base sm:text-lg font-semibold text-white mb-3 sm:mb-4">Required Information</h3>
                  
                  <div className="space-y-3 sm:space-y-4">
                    <div>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 sm:px-4 sm:py-3 border rounded-lg sm:rounded-xl text-white placeholder-slate-400 focus:ring-2 focus:border-transparent transition-all backdrop-blur-sm text-sm sm:text-base ${
                          formData.name && !validation.name 
                            ? 'border-red-400 focus:ring-red-500' 
                            : 'border-white/20 focus:ring-purple-500'
                        }`}
                        placeholder="Your full name"
                      />
                      {formData.name && !validation.name && (
                        <p className="text-red-400 text-xs sm:text-sm mt-1">Please enter a valid name</p>
                      )}
                    </div>

                    <div>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 sm:px-4 sm:py-3 border rounded-lg sm:rounded-xl text-white placeholder-slate-400 focus:ring-2 focus:border-transparent transition-all backdrop-blur-sm text-sm sm:text-base ${
                          formData.phone && !validation.phone 
                            ? 'border-red-400 focus:ring-red-500' 
                            : 'border-white/20 focus:ring-purple-500'
                        }`}
                        placeholder="(555) 123-4567"
                      />
                      {formData.phone && !validation.phone && (
                        <p className="text-red-400 text-xs sm:text-sm mt-1">Please enter a valid phone number</p>
                      )}
                    </div>
                  </div>
                  
                  {/* Show Done button when editing and fields are valid */}
                  {isEditingInfo && formData.name && formData.phone && validation.name && validation.phone && (
                    <div className="mt-4 flex justify-end">
                      <button
                        type="button"
                        onClick={() => setIsEditingInfo(false)}
                        className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors flex items-center space-x-2"
                      >
                        <Check className="w-4 h-4" />
                        <span>Done</span>
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-white/5 rounded-2xl border border-white/10">
                  <button
                    type="button"
                    onClick={() => setIsEditingInfo(true)}
                    className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-all rounded-2xl group"
                  >
                    <div className="flex items-center space-x-3">
                      <ChevronUp className="w-5 h-5 text-slate-400 group-hover:text-white transition-colors" />
                      <div className="text-left">
                        <h3 className="text-lg font-semibold text-white group-hover:text-purple-200 transition-colors">Your Information</h3>
                        <p className="text-sm text-slate-400 group-hover:text-slate-300 transition-colors">{formData.name} • {formData.phone}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="text-green-400">
                        <Check className="w-5 h-5" />
                      </div>
                      <Edit3 className="w-4 h-4 text-slate-400 group-hover:text-white transition-colors" />
                    </div>
                  </button>
                </div>
              )}

              {/* Group Information */}
              {formData.name && formData.phone && validation.name && validation.phone && (
                <div className="bg-white/5 rounded-2xl p-6 sm:p-8 border border-white/10 animate-fadeIn">
                  <h3 className="text-xl sm:text-2xl font-semibold text-white mb-6 text-center">Group Information (Optional)</h3>
                  
                  <div className="mb-6 sm:mb-8">
                    <label className="block text-base sm:text-lg font-medium text-slate-300 mb-4 text-center">
                      Signing waiver for:
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ 
                          ...prev, 
                          signedFor: 'myself',
                          numKids: 0,
                          kids: [],
                          participants: []
                        }))}
                        className={`px-4 py-4 sm:px-6 sm:py-5 rounded-2xl font-semibold transition-all text-base sm:text-lg ${
                          formData.signedFor === 'myself'
                            ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-xl scale-105'
                            : 'bg-white/10 text-slate-300 hover:bg-white/20 border border-white/20 hover:border-purple-400/30'
                        }`}
                      >
                        Myself
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ 
                          ...prev, 
                          signedFor: 'family',
                          participants: [],
                          numKids: prev.numKids === 0 ? 1 : prev.numKids,
                          kids: prev.kids.length === 0 ? [{name: '', age: 8, gender: 'neutral'}] : prev.kids
                        }))}
                        className={`px-4 py-4 sm:px-6 sm:py-5 rounded-2xl font-semibold transition-all text-base sm:text-lg ${
                          formData.signedFor === 'family'
                            ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-xl scale-105'
                            : 'bg-white/10 text-slate-300 hover:bg-white/20 border border-white/20 hover:border-purple-400/30'
                        }`}
                      >
                        My Family
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ 
                          ...prev, 
                          signedFor: 'group',
                          numKids: 0,
                          kids: [],
                          participants: prev.participants.length === 0 ? [''] : prev.participants
                        }))}
                        className={`px-4 py-4 sm:px-6 sm:py-5 rounded-2xl font-semibold transition-all text-base sm:text-lg ${
                          formData.signedFor === 'group'
                            ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-xl scale-105'
                            : 'bg-white/10 text-slate-300 hover:bg-white/20 border border-white/20 hover:border-purple-400/30'
                        }`}
                      >
                        My Group
                      </button>
                    </div>
                  </div>

                  {/* Kids Section - Only shows when My Family is selected */}
                  {formData.signedFor === 'family' && (
                    <div className="bg-white/5 rounded-xl p-4 md:p-6 mb-4">
                      <label className="block text-sm font-medium text-white mb-4">
                        👨‍👩‍👧‍👦 Kids (Ages 5-17)
                      </label>
                      <div className="space-y-4">
                        {formData.kids.map((kid, index) => (
                          <div key={index} className="bg-white/5 p-3 md:p-4 rounded-xl">
                            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                              <div className="flex items-center space-x-3 flex-1 min-w-0">
                                <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center">
                                  {kid.name && detectGender(kid.name) !== 'neutral' && (
                                    <div className="animate-fadeIn">
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const newKids = [...formData.kids];
                                          const genders: Array<'boy' | 'girl' | 'neutral'> = ['boy', 'girl', 'neutral'];
                                          const currentIndex = genders.indexOf(newKids[index].gender);
                                          newKids[index].gender = genders[(currentIndex + 1) % 3];
                                          setFormData(prev => ({ ...prev, kids: newKids }));
                                        }}
                                        className="text-2xl hover:scale-110 transition-all duration-200 hover:rotate-12"
                                        title="Click to change gender"
                                      >
                                        {getGenderIcon(kid.gender)}
                                      </button>
                                    </div>
                                  )}
                                </div>
                                <input
                                  type="text"
                                  value={kid.name}
                                  onChange={(e) => {
                                    const newKids = [...formData.kids];
                                    newKids[index].name = e.target.value;
                                    
                                    // Delayed gender detection for more interesting UX
                                    setTimeout(() => {
                                      newKids[index].gender = detectGender(e.target.value);
                                      setFormData(prev => ({ ...prev, kids: newKids }));
                                    }, e.target.value.length > 2 ? 800 : 0);
                                    
                                    setFormData(prev => ({ ...prev, kids: newKids }));
                                  }}
                                  className="flex-1 px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all backdrop-blur-sm text-sm md:text-base"
                                  placeholder="Child's name"
                                />
                              </div>
                              
                              <div className="flex items-center space-x-2 flex-shrink-0">
                                <div className="flex items-center bg-white/10 border border-white/20 rounded-xl px-3 py-2">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const newKids = [...formData.kids];
                                      newKids[index].age = Math.max(5, newKids[index].age - 1);
                                      setFormData(prev => ({ ...prev, kids: newKids }));
                                    }}
                                    className="w-8 h-8 bg-white/10 border border-white/20 rounded-lg hover:bg-white/20 transition-all backdrop-blur-sm text-white flex items-center justify-center"
                                  >
                                    <Minus className="w-4 h-4" />
                                  </button>
                                  <input
                                    type="number"
                                    min="5"
                                    max="17"
                                    value={kid.age}
                                    onChange={(e) => {
                                      const newKids = [...formData.kids];
                                      newKids[index].age = Math.max(5, Math.min(17, parseInt(e.target.value) || 8));
                                      setFormData(prev => ({ ...prev, kids: newKids }));
                                    }}
                                    className="w-12 px-2 py-1 bg-transparent text-white text-center focus:outline-none text-lg font-bold"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const newKids = [...formData.kids];
                                      newKids[index].age = Math.min(17, newKids[index].age + 1);
                                      setFormData(prev => ({ ...prev, kids: newKids }));
                                    }}
                                    className="w-8 h-8 bg-white/10 border border-white/20 rounded-lg hover:bg-white/20 transition-all backdrop-blur-sm text-white flex items-center justify-center"
                                  >
                                    <Plus className="w-4 h-4" />
                                  </button>
                                </div>
                                <span className="text-slate-300 text-sm font-medium">yrs</span>
                                
                                {formData.kids.length > 1 && (
                                  <button
                                    type="button"
                                    onClick={() => setFormData(prev => ({ 
                                      ...prev, 
                                      kids: prev.kids.filter((_, i) => i !== index)
                                    }))}
                                    className="w-8 h-8 bg-gradient-to-r from-red-500 to-rose-500 rounded-full flex items-center justify-center hover:shadow-lg hover:scale-105 transition-all text-white ml-2"
                                    title="Remove this child"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                        
                        {/* Add Child Button - Bottom positioned and less CTA-like */}
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ 
                            ...prev, 
                            kids: [...prev.kids, {name: '', age: 8, gender: 'neutral'}]
                          }))}
                          className="w-full mt-3 p-4 bg-white/5 border-2 border-dashed border-white/20 rounded-xl hover:bg-white/10 hover:border-white/30 transition-all backdrop-blur-sm text-white flex items-center justify-center space-x-2 group"
                        >
                          <div className="w-8 h-8 bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-full flex items-center justify-center group-hover:from-green-500/30 group-hover:to-emerald-500/30 transition-all">
                            <Plus className="w-4 h-4 text-green-400" />
                          </div>
                          <span className="text-slate-300 group-hover:text-white transition-colors">Add another child</span>
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Participants Section - Only shows when "My Group" is selected */}
                  {formData.signedFor === 'group' && (
                    <div className="bg-white/5 rounded-xl p-4 md:p-6">
                      <label className="block text-sm font-medium text-white mb-4">
                        👥 Participants
                      </label>
                      <div className="space-y-4">
                        {formData.participants.map((name, index) => (
                          <div key={index} className="bg-white/5 p-3 md:p-4 rounded-xl">
                            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                              <div className="flex items-center space-x-3 flex-1 min-w-0">
                                <span className="flex-shrink-0 w-8 h-8 bg-purple-500/20 rounded-full flex items-center justify-center text-purple-400 font-medium text-sm">
                                  {index + 1}
                                </span>
                                <input
                                  type="text"
                                  value={name}
                                  onChange={(e) => {
                                    const newParticipants = [...formData.participants];
                                    newParticipants[index] = e.target.value;
                                    setFormData(prev => ({ ...prev, participants: newParticipants }));
                                  }}
                                  className="flex-1 px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all backdrop-blur-sm text-sm md:text-base"
                                  placeholder="Participant name"
                                />
                              </div>
                              
                              <div className="flex items-center space-x-2 flex-shrink-0">
                                {formData.participants.length > 1 && (
                                  <button
                                    type="button"
                                    onClick={() => setFormData(prev => ({ 
                                      ...prev, 
                                      participants: prev.participants.filter((_, i) => i !== index)
                                    }))}
                                    className="w-8 h-8 bg-gradient-to-r from-red-500 to-rose-500 rounded-full flex items-center justify-center hover:shadow-lg hover:scale-105 transition-all text-white"
                                    title="Remove this participant"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                        
                        {/* Add Participant Button - Bottom positioned and less CTA-like */}
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ 
                            ...prev, 
                            participants: [...prev.participants, '']
                          }))}
                          className="w-full mt-3 p-4 bg-white/5 border-2 border-dashed border-white/20 rounded-xl hover:bg-white/10 hover:border-white/30 transition-all backdrop-blur-sm text-white flex items-center justify-center space-x-2 group"
                        >
                          <div className="w-8 h-8 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-full flex items-center justify-center group-hover:from-blue-500/30 group-hover:to-purple-500/30 transition-all">
                            <Plus className="w-4 h-4 text-blue-400" />
                          </div>
                          <span className="text-slate-300 group-hover:text-white transition-colors">Add another participant</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Complete Button */}
              <div className="text-center">
                <button
                  onClick={handleCompleteWaiver}
                  disabled={!canCompleteWaiver()}
                  className={`px-8 py-4 rounded-xl font-semibold text-lg transition-all ${
                    canCompleteWaiver()
                      ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700 shadow-lg hover:shadow-xl transform hover:-translate-y-1'
                      : 'bg-slate-600 text-slate-400 cursor-not-allowed'
                  }`}
                >
                  Complete Waiver
                </button>
              </div>
            </div>
          </div>
        );

      case 'signature':
        return (
          <div className="space-y-6">
            {/* Back Button */}
            <div className="flex justify-start">
              <button
                onClick={() => setCurrentStep('form')}
                className="flex items-center text-slate-300 hover:text-white transition-colors group"
              >
                <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" />
                <span>Back to Form</span>
              </button>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-r from-pink-500 to-purple-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <Sparkles className="w-10 h-10 text-white animate-pulse" />
              </div>
              <h2 className="text-3xl font-bold text-white mb-4">Final Step!</h2>
              <p className="text-slate-300 text-lg">Sign below to complete your waiver</p>
            </div>

            <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
              <div className="bg-white/10 border-2 border-dashed border-white/20 rounded-xl p-6">
                <p className="text-sm text-slate-300 mb-4 text-center">
                  Please sign in the box below using your mouse, trackpad, or finger
                </p>
                
                <div className="border border-white/30 rounded-xl overflow-hidden bg-white">
                  <SignatureCanvas
                    ref={signaturePadRef}
                    canvasProps={{
                      className: 'signature-canvas w-full',
                      width: 500,
                      height: 200,
                    }}
                    backgroundColor="white"
                  />
                </div>
                
                <div className="flex justify-center mt-4">
                  <button
                    type="button"
                    onClick={() => signaturePadRef.current?.clear()}
                    className="flex items-center px-6 py-3 text-sm text-slate-300 bg-white/10 border border-white/20 rounded-xl hover:bg-white/20 transition-all backdrop-blur-sm"
                  >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Clear Signature
                  </button>
                </div>
              </div>

              <div className="mt-6 text-center">
                <button
                  onClick={handleSubmit}
                  disabled={!canSubmit() || loading}
                  className={`px-8 py-4 rounded-xl font-semibold text-lg transition-all ${
                    canSubmit() && !loading
                      ? 'bg-gradient-to-r from-green-600 to-blue-600 text-white hover:from-green-700 hover:to-blue-700 shadow-lg hover:shadow-xl transform hover:-translate-y-1'
                      : 'bg-slate-600 text-slate-400 cursor-not-allowed'
                  }`}
                >
                  {loading ? 'Submitting...' : 'Submit Waiver & Start VR Experience!'}
                </button>
              </div>
            </div>
          </div>
        );

      case 'complete':
        return (
          <div className="space-y-6 sm:space-y-8">
            {/* Enhanced Progress Indicator */}
            <motion.div 
              className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4 mb-6"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              {/* Step 1 - Waiver Complete */}
              <motion.div 
                className="flex flex-col sm:flex-row items-center"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <motion.div 
                  className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r from-green-500 to-green-600 rounded-full flex items-center justify-center shadow-lg mb-2 sm:mb-0"
                  animate={{ 
                    scale: [1, 1.1, 1],
                    boxShadow: ["0 4px 20px rgba(34, 197, 94, 0.3)", "0 8px 30px rgba(34, 197, 94, 0.6)", "0 4px 20px rgba(34, 197, 94, 0.3)"]
                  }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <Check className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                </motion.div>
                <span className="text-green-300 text-sm sm:text-base font-semibold sm:ml-3">
                  Waiver Complete
                </span>
              </motion.div>

              {/* Connector 1 */}
              <motion.div 
                className="hidden sm:block w-12 h-0.5 bg-gradient-to-r from-green-500 to-blue-500"
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ duration: 0.8, delay: 0.8 }}
              />

              {/* Step 2 - Check-In Ready */}
              <motion.div 
                className="flex flex-col sm:flex-row items-center"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.6, delay: 0.6 }}
              >
                <motion.div 
                  className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full flex items-center justify-center shadow-lg mb-2 sm:mb-0"
                  animate={{ 
                    scale: [1, 1.1, 1],
                    boxShadow: ["0 4px 20px rgba(59, 130, 246, 0.3)", "0 8px 30px rgba(59, 130, 246, 0.6)", "0 4px 20px rgba(59, 130, 246, 0.3)"]
                  }}
                  transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
                >
                  <Check className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                </motion.div>
                <span className="text-blue-300 text-sm sm:text-base font-semibold sm:ml-3">
                  Check-In Ready
                </span>
              </motion.div>

              {/* Connector 2 */}
              <motion.div 
                className="hidden sm:block w-12 h-0.5 bg-gradient-to-r from-blue-500 to-purple-500"
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ duration: 0.8, delay: 1.2 }}
              />

              {/* Step 3 - VR Adventure */}
              <motion.div 
                className="flex flex-col sm:flex-row items-center"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.6, delay: 1.0 }}
              >
                <motion.div 
                  className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center shadow-lg mb-2 sm:mb-0"
                  animate={{ 
                    scale: [1, 1.2, 1],
                    rotate: [0, 360, 0],
                    boxShadow: [
                      "0 4px 20px rgba(168, 85, 247, 0.4)", 
                      "0 8px 40px rgba(168, 85, 247, 0.8)", 
                      "0 4px 20px rgba(168, 85, 247, 0.4)"
                    ]
                  }}
                  transition={{ 
                    duration: 3, 
                    repeat: Infinity,
                    delay: 1,
                    ease: "easeInOut"
                  }}
                >
                  <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                </motion.div>
                <motion.span 
                  className="text-purple-300 text-sm sm:text-base font-bold sm:ml-3"
                  animate={{ 
                    textShadow: [
                      "0 0 10px rgba(168, 85, 247, 0.5)",
                      "0 0 20px rgba(168, 85, 247, 1)",
                      "0 0 10px rgba(168, 85, 247, 0.5)"
                    ]
                  }}
                  transition={{ duration: 2, repeat: Infinity, delay: 1.5 }}
                >
                  🎮 VR Adventure!
                </motion.span>
              </motion.div>
            </motion.div>

            {/* Enhanced Success Header */}
            <div className="text-center">
              <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-r from-green-500 to-blue-500 rounded-full flex items-center justify-center mx-auto animate-pulse mb-4 sm:mb-6">
                <Check className="w-10 h-10 sm:w-12 sm:h-12 text-white" />
              </div>
              {completedWaiverData?.isReturningCustomer ? (
                <>
                  <h2 className="text-3xl sm:text-4xl font-bold text-white mb-2 sm:mb-3">👋 Welcome back, {completedWaiverData?.name}!</h2>
                  <p className="text-green-300 text-lg sm:text-xl mb-2 px-2">Your waiver is still valid - let's continue the adventure!</p>
                  <p className="text-slate-300 text-sm px-2">
                    Valid until {completedWaiverData?.expiresAt ? new Date(completedWaiverData.expiresAt).toLocaleDateString() : 'next year'}
                  </p>
                </>
              ) : (
                <>
                  <h2 className="text-3xl sm:text-4xl font-bold text-white mb-2 sm:mb-3">🎉 Welcome, {completedWaiverData?.name || 'adventurer'}!</h2>
                  <p className="text-green-300 text-lg sm:text-xl mb-2 px-2">You're checked in and ready to go!</p>
                  <p className="text-slate-300 text-sm px-2">
                    Valid until {completedWaiverData ? new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toLocaleDateString() : 'next year'}
                  </p>
                </>
              )}
            </div>

            {/* Priority QR Code Section */}
            <div className="bg-gradient-to-br from-white/15 to-white/5 rounded-2xl p-6 border-2 border-white/30 shadow-2xl">
              <div className="text-center mb-6">
                <h3 className="text-2xl sm:text-3xl font-bold text-white mb-2">📱 Show This to Staff</h3>
                <p className="text-blue-200 text-base sm:text-lg">Quick check-in • No waiting</p>
                <p className="text-blue-300 text-sm mt-2">Staff can scan this QR code to instantly verify your waiver</p>
              </div>
              
              {qrCodeUrl && (
                <div className="flex justify-center mb-6">
                  <div className="bg-white p-4 sm:p-6 rounded-2xl shadow-lg">
                    <img src={qrCodeUrl} alt="Waiver Verification QR Code" className="w-40 h-40 sm:w-56 sm:h-56" />
                  </div>
                </div>
              )}

              {/* Editable Waiver Details */}
              {completedWaiverData && (
                <div className="bg-white/10 rounded-xl p-4 backdrop-blur-sm border border-white/20">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-white font-semibold">Waiver Information</h4>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setIsEditingInfo(!isEditingInfo)}
                        className="px-3 py-1 bg-blue-500/20 text-blue-300 rounded-lg text-sm hover:bg-blue-500/30 transition-colors"
                      >
                        {isEditingInfo ? 'Save' : 'Edit'}
                      </button>
                      {completedWaiverData?.isReturningCustomer && (
                        <button
                          onClick={() => {
                            // Reset to fresh waiver form
                            setCurrentStep('waiver');
                            setCompletedWaiverData(null);
                            setQrCodeUrl('');
                          }}
                          className="px-3 py-1 bg-orange-500/20 text-orange-300 rounded-lg text-sm hover:bg-orange-500/30 transition-colors"
                        >
                          Renew Waiver
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-3 text-sm sm:text-base">
                    {/* Name - Always editable */}
                    <div className="flex items-center space-x-3">
                      <User className="w-4 h-4 text-blue-300 flex-shrink-0" />
                      <span className="text-slate-300 w-16">Name:</span>
                      {isEditingInfo ? (
                        <input
                          type="text"
                          value={formData.name}
                          onChange={(e) => setFormData(prev => ({...prev, name: e.target.value}))}
                          className="bg-white/10 text-white px-2 py-1 rounded border border-white/20 flex-1"
                        />
                      ) : (
                        <span className="text-white font-semibold">{completedWaiverData.name}</span>
                      )}
                    </div>

                    {/* Email - Editable if provided */}
                    {(completedWaiverData.email || formData.email) && (
                      <div className="flex items-center space-x-3">
                        <Mail className="w-4 h-4 text-green-300 flex-shrink-0" />
                        <span className="text-slate-300 w-16">Email:</span>
                        {isEditingInfo ? (
                          <input
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData(prev => ({...prev, email: e.target.value}))}
                            className="bg-white/10 text-white px-2 py-1 rounded border border-white/20 flex-1"
                          />
                        ) : (
                          <span className="text-white font-semibold">{completedWaiverData.email || formData.email}</span>
                        )}
                      </div>
                    )}

                    {/* Group Type - Show only if relevant */}
                    {completedWaiverData.signedFor !== 'myself' && (
                      <div className="flex items-center space-x-3">
                        <Users className="w-4 h-4 text-green-300 flex-shrink-0" />
                        <span className="text-slate-300 w-16">Group:</span>
                        <span className="text-white font-semibold">
                          {completedWaiverData.signedFor === 'family' 
                            ? `Family ${completedWaiverData.numKids ? `(${completedWaiverData.numKids} kids)` : ''}`
                            : completedWaiverData.signedFor === 'group' 
                              ? `Group ${completedWaiverData.numParticipants ? `(${completedWaiverData.numParticipants} people)` : ''}`
                              : 'Individual'
                          }
                        </span>
                      </div>
                    )}

                    {/* Valid Until */}
                    <div className="flex items-center space-x-3">
                      <Calendar className="w-4 h-4 text-purple-300 flex-shrink-0" />
                      <span className="text-slate-300 w-16">Valid:</span>
                      <span className="text-white font-semibold">
                        {completedWaiverData?.expiresAt 
                          ? new Date(completedWaiverData.expiresAt).toLocaleDateString('en-US', { 
                              year: 'numeric', 
                              month: 'long' 
                            })
                          : new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', { 
                              year: 'numeric', 
                              month: 'long' 
                            })
                        }
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Birthday Section - Simplified for Primary User */}
            {!completedWaiverData?.isReturningCustomer && (
              <div className="bg-gradient-to-r from-yellow-500/20 to-orange-500/20 rounded-xl p-4 border border-yellow-400/30">
                <div className="text-center mb-4">
                  <h4 className="text-lg font-bold text-white mb-2">🎂 Birthday Specials!</h4>
                  <p className="text-yellow-200 text-sm">Get FREE VR sessions on birthdays</p>
                </div>

                <div className="max-w-lg mx-auto">
                  {/* Primary Birthday */}
                  <div>
                    <label className="block text-yellow-200 text-sm mb-2">
                      {formData.signedFor === 'family' ? 'Parent/Guardian Birthday' : 'Your Birthday'}
                    </label>
                    <input
                      type="date"
                      value={formData.birthday}
                      onChange={(e) => setFormData(prev => ({...prev, birthday: e.target.value}))}
                      className="w-full px-3 py-2 bg-white/10 border border-yellow-400/30 rounded-lg text-white focus:ring-2 focus:ring-yellow-500"
                    />
                    <p className="text-yellow-300 text-xs text-center mt-2">
                      🎉 Perfect for special birthday visits!
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Email Signup Section - Only if not already provided from completed waiver */}
            {!completedWaiverData?.email && (
              <div className="bg-gradient-to-r from-green-500/20 to-blue-500/20 rounded-xl p-6 border border-green-400/30">
                <div className="text-center mb-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-green-400 to-blue-400 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Gift className="w-6 h-6 text-white" />
                  </div>
                  <h4 className="text-xl font-bold text-white mb-2">🎁 Get Your Discount Code!</h4>
                  <p className="text-green-200 text-sm">Join 3,200+ players who get VR perks first</p>
                </div>
                
                {!emailSignupSuccess ? (
                  <div className="max-w-md mx-auto space-y-4">
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 border-2 rounded-xl text-white placeholder-slate-300 focus:ring-2 focus:border-transparent transition-all backdrop-blur-sm text-center ${
                        formData.email && !validation.email 
                          ? 'border-red-400 focus:ring-red-500' 
                          : 'border-green-400/50 focus:ring-green-500'
                      }`}
                      placeholder="your@email.com"
                    />
                    {formData.email && !validation.email && (
                      <p className="text-red-400 text-sm text-center">Please enter a valid email address</p>
                    )}
                    
                    <button 
                      onClick={() => {
                        if (formData.email && validation.email) {
                          setEmailSignupSuccess(true);
                        }
                      }}
                      disabled={!formData.email || !validation.email}
                      className={`w-full py-4 rounded-xl font-bold text-lg transition-all shadow-lg transform hover:-translate-y-1 ${
                        formData.email && validation.email
                          ? 'bg-gradient-to-r from-green-500 to-blue-500 text-white hover:from-green-600 hover:to-blue-600'
                          : 'bg-slate-600 text-slate-400 cursor-not-allowed'
                      }`}
                    >
                      {formData.email && validation.email 
                        ? '🎁 Get My 15% Off Code!'
                        : '✉️ Enter valid email to unlock discount'
                      }
                    </button>
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Check className="w-8 h-8 text-white" />
                    </div>
                    <h4 className="text-xl font-bold text-white mb-2">🎉 Your 15% off code: WAIVER15</h4>
                    <p className="text-green-200 text-sm">Check your email for more exclusive perks!</p>
                  </div>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/')}
                className="bg-gradient-to-r from-green-600 to-blue-600 text-white px-8 py-4 rounded-xl hover:from-green-700 hover:to-blue-700 transition-all font-bold text-lg shadow-lg transform hover:-translate-y-1"
              >
                🎮 Start VR Adventure
              </button>
              
              <button
                onClick={() => {
                  if (navigator.share) {
                    navigator.share({
                      title: 'Immersa VR Waiver',
                      text: 'I just signed my VR waiver at Immersa!',
                      url: window.location.href
                    });
                  } else {
                    navigator.clipboard.writeText(window.location.href);
                    alert('Link copied to clipboard!');
                  }
                }}
                className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8 py-4 rounded-xl hover:from-purple-700 hover:to-pink-700 transition-all font-bold text-lg shadow-lg transform hover:-translate-y-1"
              >
                📱 Share with Friends
              </button>
            </div>

            {/* Enhanced Pro Tip */}
            <div className="bg-gradient-to-r from-green-500/15 to-blue-500/15 rounded-xl p-4 border border-green-500/30">
              <div className="text-center">
                <h4 className="text-green-300 font-bold mb-2">🚀 You're All Set!</h4>
                <p className="text-green-200 text-sm mb-2">
                  Show your QR code to staff → Get geared up → Dive into VR!
                </p>
                <p className="text-green-400 text-xs">
                  💡 <strong>Pro Tip:</strong> Next time, just enter your phone number to skip the paperwork!
                </p>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <style>{styles}</style>
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-2 sm:p-4 lg:p-6 xl:p-8">
        <div className="relative min-h-screen flex items-center">
          <div className="absolute inset-0 bg-gradient-to-r from-purple-600/10 to-pink-600/10"></div>
          <div className="relative w-full max-w-2xl lg:max-w-3xl xl:max-w-4xl mx-auto py-4 sm:py-6 lg:py-8">
            {/* Header */}
            <div className="mb-4 sm:mb-6 lg:mb-8 px-2">
              <button
                onClick={() => navigate('/')}
                className="flex items-center text-slate-300 hover:text-white mb-3 sm:mb-4 lg:mb-6 transition-colors group"
              >
                <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5 mr-2 group-hover:-translate-x-1 transition-transform" />
                <span className="text-sm sm:text-base">Back to Home</span>
              </button>
            </div>

            {/* Main Content */}
            <div className="bg-white/10 backdrop-blur-lg rounded-xl sm:rounded-2xl lg:rounded-3xl shadow-2xl p-3 sm:p-6 lg:p-8 border border-white/20 mx-2 sm:mx-0">
              {error && (
                <div className="bg-red-500/10 border border-red-400/30 text-red-300 px-3 py-2 sm:px-6 sm:py-4 rounded-lg sm:rounded-2xl mb-4 sm:mb-6 backdrop-blur-sm">
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-red-400 rounded-full mr-3"></div>
                    <span className="text-xs sm:text-base">{error}</span>
                  </div>
                </div>
              )}

              {renderContent()}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default StreamlinedWaiverForm;